import { useState, useEffect } from "react";
import { Drawer, Form, InputNumber, Checkbox, Button, Dropdown, Select, message, Slider } from "antd";
import { RgbaStringColorPicker } from "react-colorful";
import { BgColorsOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { slideAction, saveMirrorToCloudinary } from "../../store/slideSlice";
import { deleteElement, findElementType, getUpdatedSlides } from "../../utils/helper";
import { slideAPI } from "../../store/api";
import { getSnapshot } from "../../utils/utils";
import { emitElementDelete, emitSlideAdd } from "../../Socket/Socket";
import "./propertiesDrawer.css";
import { Shape } from "../Slide/SlideContents/shapes/Shape";
import { Image } from "../Slide/SlideContents/Image/Image";

const ColorDropdown = ({ color, onChange, tooltip = "Pick Color", disabled = false }) => (
    <Dropdown
        overlay={
            <div className="color-picker-menu">
                <RgbaStringColorPicker color={color} onChange={onChange} />
            </div>
        }
        trigger={["click"]}
        disabled={disabled}
        placement="bottomLeft"
    >
        <a className="color-dropdown-trigger" onClick={(e) => e.preventDefault()} aria-label={tooltip}>
            <div title={tooltip}>
                <BgColorsOutlined className="color-dropdown-icon" />
            </div>
        </a>
    </Dropdown>
);

const PropertiesDrawer = ({ isModalVisible, setShowPropertiesModal, elemRef }) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [bgColor, setBgColor] = useState("rgba(255,255,255,1)");
    const [borderColor, setBorderColor] = useState("rgba(0,0,0,1)");
    const [strokeUnit, setStrokeUnit] = useState("px");
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [opacityValue, setOpacityValue] = useState(100);
    const [borderRadiusUnit, setBorderRadiusUnit] = useState("px");
    const [rotationValue, setRotationValue] = useState(0);
    const [borderStyle, setBorderStyle] = useState("solid");
    const [previewValues, setPreviewValues] = useState(null);

    const { slides: existingSlides, mirrorSlides } = useSelector((state) => state.slide);
    const slideId = useSelector((state) => state.slide.currentSlideId);
    const elementId = elemRef.current?.id || null;
    const elementType = findElementType(elemRef?.current);

    const currentSlideData = existingSlides?.[slideId] || {};
    const elementStyles = currentSlideData?.additionalData?.elementsData?.[elementId]?.properties || {};
    const shapeStyles = currentSlideData?.shapes?.[elementId]?.styles || {};
    const imageStyles = currentSlideData?.image?.[elementId]?.styles || {};
    let currentStyles = {};
    let initialValues = {};

    useEffect(() => {
        if (isModalVisible && elementId) {
            currentStyles =
                elementType === 'shape' ? shapeStyles :
                    elementType === 'image' ? imageStyles :
                        {};

            initialValues = {
                ...elementStyles,
                width: currentStyles?.width || (elementType === 'image' ? 30 : 100),
                height: currentStyles?.height || (elementType === 'image' ? 30 : 100),
                isTransparent: elementStyles?.isTransparent || false,
                strokeWidth: elementStyles?.strokeWidth || (elementType === 'image' ? 0 : 0.5),
                backgroundColor: elementStyles?.backgroundColor || "rgba(255,255,255,1)",
                borderColor: elementStyles?.borderColor || (elementType === 'image' ? '#ffff' : "rgba(0,0,0,1)"),
                strokeUnit: elementStyles?.strokeUnit || "px",
                borderRadius: elementStyles?.borderRadius || 0,
                borderRadiusUnit: elementStyles?.borderRadiusUnit || "px",
                borderStyle: elementStyles?.borderStyle || "solid",
                opacity: elementStyles?.opacity !== undefined ? elementStyles.opacity : 100,
                rotation: elementStyles?.rotation || 0,
            };
            const width = extractNumberFromString(initialValues.width);
            const height = extractNumberFromString(initialValues.height);
            const strokeWidth = extractNumberFromString(initialValues.strokeWidth);
            const borderRadius = extractNumberFromString(initialValues.borderRadius);

            form.setFieldsValue({
                ...initialValues,
                width: !isNaN(width) ? parseFloat(Number(width).toFixed(3)) : 100,
                height: !isNaN(height) ? parseFloat(Number(height).toFixed(3)) : 100,
                strokeWidth: !isNaN(strokeWidth) ? parseFloat(Number(strokeWidth).toFixed(3)) : 0.5,
                borderRadius: !isNaN(borderRadius) ? parseFloat(Number(borderRadius).toFixed(3)) : 0,
                opacity: initialValues.opacity,
            });
            setBgColor(initialValues.backgroundColor);
            setBorderColor(initialValues.borderColor);
            setStrokeUnit(initialValues.strokeUnit);
            setOpacityValue(initialValues.opacity);
            setBorderRadiusUnit(initialValues.borderRadiusUnit);
            setRotationValue(initialValues.rotation);
            setBorderStyle(initialValues.borderStyle);
        }

        return () => {
            form.resetFields();
            setPreviewValues(null);
        };
    }, [isModalVisible, elementId]);

    const updateSlideData = async (updatedData) => {
        try {
            const slideData = {
                ...currentSlideData,
                additionalData: {
                    ...currentSlideData.additionalData,
                    elementsData: {
                        ...currentSlideData.additionalData?.elementsData,
                        [elementId]: {
                            properties: {
                                ...updatedData,
                                height: `${updatedData.height}%`,
                                width: `${updatedData.width}%`,
                                opacity: updatedData.opacity,
                                borderStyle: updatedData.borderStyle,
                                borderRadius: updatedData.borderRadius,
                                borderRadiusUnit: updatedData.borderRadiusUnit
                            },
                        },
                    },
                },
            };

            dispatch(slideAction.addSlide({ slideId, slideData }));

            const dataUrl = await getSnapshot();
            dispatch(saveMirrorToCloudinary({ dataId: slideId, mirrorSlideDataUrl: dataUrl, presentationId: id }));

            emitSlideAdd({
                roomId: id,
                mirrorData: { dataId: slideId, mirrorSlideDataUrl: dataUrl },
                slideData: { slideId, slideData },
            });

            const updatedSlides = getUpdatedSlides(slideData, 'slide', existingSlides);
            await slideAPI.addNewSlideInDatabase(
                id, slideId, slideData, dispatch,
                { slides: updatedSlides, mirrorSlides },
            );
        } catch (error) {
            message.error("Failed to update slide. Please try again.");
            throw error;
        }
    };

    const handleUpdate = async () => {
        try {
            setIsUpdating(true);
            setIsDeleting(false);
            const values = await form.validateFields();
            await updateSlideData({
                ...values,
                backgroundColor: bgColor,
                borderColor,
                strokeUnit,
                elementId,
                borderRadiusUnit,
                borderStyle,
            });
            // setShowPropertiesModal(false);
        } catch (error) {
            message.error("Invalid input. Please check the form.");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = async () => {
        try {
            setIsDeleting(true);
            setIsUpdating(false);
            const payload = { elementId, slideId };
            const updatedSlides = deleteElement(existingSlides, payload, 'shape');

            await slideAPI.deleteElementFromDatabase(
                id, slideId, elementId, elementType, dispatch,
                { slides: updatedSlides, mirrorSlides },
            );

            elementType === 'shape' && dispatch(slideAction.removeShape({ ...payload, shapeId: elementId }));
            elementType === 'image' && dispatch(slideAction.removeImage({ slideId: slideId, imageId: elementId }));

            const dataUrl = await getSnapshot();
            dispatch(saveMirrorToCloudinary({ dataId: slideId, mirrorSlideDataUrl: dataUrl, presentationId: id }));

            emitElementDelete({ roomId: id, elementType: elementType, elementData: payload });
            setShowPropertiesModal(false);
        } catch (error) {
            message.error("Failed to delete element. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    const extractNumberFromString = (value) => {
        if (typeof value === 'string') {
            if (value.includes('%') || value.includes('px') || value.includes('rem')) {
                return parseFloat(value.replace(/[%pxrem]/g, ''));
            }
            return parseFloat(value);
        }
        return value;
    };

    useEffect(() => {
        const values = form.getFieldsValue(true);
        setPreviewValues({
            ...values,
            backgroundColor: bgColor,
            borderColor,
            strokeUnit,
            elementId,
            borderRadiusUnit,
            borderStyle,
        });
    }, [form, bgColor, borderColor, strokeUnit, elementId, borderRadiusUnit, borderStyle]);

    if (!elementId || !isModalVisible) {
        return null;
    }

    return (
        <Drawer
            title={`${elementType?.charAt(0).toUpperCase() + elementType?.slice(1)} Properties`}
            placement="right"
            width={360}
            onClose={() => setShowPropertiesModal(false)}
            visible={isModalVisible}
            destroyOnClose
            className="properties-drawer"
            footer={
                <div className="drawer-footer">
                    <Button
                        className={`footer-btn delete-btn btn-style ${isDeleting || isUpdating ? 'disable' : ''}`}
                        onClick={handleDelete}
                    // disabled={isDeleting || isUpdating}
                    >
                        Delete
                    </Button>
                    <Button
                        className={`footer-btn update-btn btn-style ${isDeleting || isUpdating ? 'disable' : ''}`}
                        onClick={handleUpdate}
                        type="primary"
                    // disabled={isDeleting || isUpdating}
                    >
                        Update
                    </Button>
                </div>
            }
        >
            <Form
                layout="horizontal"
                form={form}
                initialValues={initialValues}
                className="properties-form"
                onValuesChange={() => {
                    const values = form.getFieldsValue(true);
                    setPreviewValues({
                        ...values,
                        backgroundColor: bgColor,
                        borderColor,
                        strokeUnit,
                        elementId,
                        borderRadiusUnit,
                        borderStyle,
                    });
                }}
            >
                <div className="section-title">Dimensions</div>
                <div className="dimensions-group">
                    <div className="dimensions-labels">
                        <label>Width</label>
                        <label>Height</label>
                    </div>
                    <div className="dimensions-fields">
                        <Form.Item
                            name="width"
                            rules={[{ required: true, message: "Width is required" }]}
                            className="dimensions-item"
                        >
                            <InputNumber
                                min={1}
                                precision={3}
                                formatter={(value) => {
                                    const num = extractNumberFromString(value);
                                    return !isNaN(num) ? parseFloat(Number(num).toFixed(3)) : '';
                                }}
                                parser={(value) => {
                                    const num = extractNumberFromString(value);
                                    return !isNaN(num) ? parseFloat(num) : 0;
                                }}
                                addonAfter="px"
                            />
                        </Form.Item>
                        <Form.Item
                            name="height"
                            rules={[{ required: true, message: "Height is required" }]}
                            className="dimensions-item"
                        >
                            <InputNumber
                                min={10}
                                precision={3}
                                formatter={(value) => {
                                    const num = extractNumberFromString(value);
                                    return !isNaN(num) ? parseFloat(Number(num).toFixed(3)) : '';
                                }}
                                parser={(value) => {
                                    const num = extractNumberFromString(value);
                                    return !isNaN(num) ? parseFloat(num) : 0;
                                }}
                                addonAfter="px"
                            />
                        </Form.Item>
                    </div>
                </div>

                <div className="section-divider" />

                <div className="section-title">Appearance</div>
                <div className="slider-row">
                    <div className="slider-header" style={{ marginTop: '6px' }}>
                        <label className="slider-label">Opacity</label>
                        <span className="slider-value">{opacityValue}%</span>
                    </div>
                    <Form.Item name="opacity" noStyle>
                        <Slider
                            min={0}
                            max={100}
                            step={1}
                            onChange={(value) => {
                                setOpacityValue(value);
                                form.setFieldsValue({ opacity: value });
                            }}
                            tooltip={{ formatter: (value) => `${value}%` }}
                        />
                    </Form.Item>
                </div>
                <div className="slider-row">
                    <div className="slider-header">
                        <label className="slider-label">Rotation</label>
                        <span className="slider-value">{rotationValue}°</span>
                    </div>
                    <Form.Item name="rotation" noStyle>
                        <Slider
                            min={0}
                            max={360}
                            step={1}
                            onChange={(value) => {
                                setRotationValue(value);
                                form.setFieldsValue({ rotation: value });
                            }}
                            tooltip={{ formatter: (value) => `${value}°` }}
                        />
                    </Form.Item>
                </div>
                {elementType === 'image' && (
                    <>
                        <div className="slider-header">
                            <label className="slider-label">Border Radius</label>
                        </div>
                        <Form.Item className="compact-form-item" colon={false}>
                            <div className="stroke-width-container">
                                <Form.Item
                                    name="borderRadius"
                                    noStyle
                                    rules={[{ required: true, message: "Required" }]}
                                >
                                    <InputNumber
                                        min={0}
                                        step={0.1}
                                        precision={2}
                                        className="stroke-width-input"
                                    />
                                </Form.Item>
                                <Form.Item name="borderRadiusUnit" noStyle>
                                    <Select
                                        value={borderRadiusUnit}
                                        onChange={setBorderRadiusUnit}
                                        options={[
                                            { value: "px", label: "px" },
                                            { value: "%", label: "%" },
                                            { value: "rem", label: "rem" },
                                        ]}
                                        className="stroke-unit-select"
                                    />
                                </Form.Item>
                            </div>
                        </Form.Item>

                    </>
                )}

                {elementType === 'shape' && (
                    <>
                        <div className="slider-header">
                            <label className="slider-label">Background Color</label>
                            <div className="color-preview" style={{ backgroundColor: bgColor }} />
                        </div>
                        <Form.Item name="backgroundColor" className="compact-form-item" colon={false}>
                            <div className="color-picker-container">
                                <ColorDropdown color={bgColor} onChange={setBgColor} tooltip="Background Color" />
                            </div>
                        </Form.Item>
                    </>
                )}

                <div className="slider-header">
                    <label className="slider-label">Border Color</label>
                    <div className="color-preview" style={{ backgroundColor: borderColor }} />
                </div>
                <Form.Item name="borderColor" className="compact-form-item" colon={false}>
                    <div className="color-picker-container">
                        <ColorDropdown color={borderColor} onChange={setBorderColor} tooltip="Border Color" />
                    </div>
                </Form.Item>

                <div className="slider-header">
                    <label className="slider-label">Border Width</label>
                </div>
                <Form.Item className="compact-form-item" colon={false}>
                    <div className="stroke-width-container">
                        <Form.Item
                            name="strokeWidth"
                            noStyle
                            rules={[{ required: true, message: "Required" }]}
                        >
                            <InputNumber
                                min={0}
                                step={0.1}
                                precision={2}
                                className="stroke-width-input"
                            />
                        </Form.Item>
                        <Form.Item name="strokeUnit" noStyle>
                            <Select
                                value={strokeUnit}
                                onChange={setStrokeUnit}
                                options={[
                                    { value: "px", label: "px" },
                                    { value: "rem", label: "rem" },
                                    elementType === 'shape' ? { value: "%", label: "%" } : null,
                                ].filter(Boolean)}
                                className="stroke-unit-select"
                            />
                        </Form.Item>

                    </div>
                </Form.Item>
                {elementType === 'image' &&
                    <>
                        <div className="slider-header">
                            <label className="slider-label">Border Style</label>
                        </div>
                        <Form.Item name="borderStyle" className="compact-form-item" colon={false}>
                            <Select
                                value={borderStyle}
                                onChange={setBorderStyle}
                                options={[
                                    { value: "solid", label: "Solid" },
                                    { value: "dashed", label: "Dashed" },
                                    { value: "dotted", label: "Dotted" },
                                    { value: "groove", label: "Groove" },
                                    { value: "double", label: "Double" },
                                    { value: "ridge", label: "Ridge" },
                                    { value: "none", label: "None" },
                                ]}
                                className="stroke-unit-select"
                            />
                        </Form.Item>
                    </>
                }
                {elementType === 'shape' &&
                    <Form.Item name="isTransparent" valuePropName="checked" className="checkbox-item">
                        <Checkbox>Transparent</Checkbox>
                    </Form.Item>
                }
                <div className="section-title">Preview</div>
                <div className="property-preview-container">
                    {elementType === 'shape' && previewValues && (
                        <Shape
                            shapeId={elementId}
                            currentSlideIdProp={slideId}
                            isPropertyPreviewMode={true}
                            previewModeProperies={previewValues}
                        />
                    )}
                    {elementType === 'image' && previewValues && (
                        <Image
                            imgId={elementId}
                            currentSlideIdProp={slideId}
                            isPropertyPreviewMode={true}
                            previewModeProperies={previewValues}
                        />
                    )}
                </div>
            </Form>
        </Drawer>
    );
};

export default PropertiesDrawer;